import { useState, type ReactElement } from 'react';
import type { BrunelActivityIndicator } from '../lib/cockpit-view';
import { liveDockCensusLabel, type LiveDockCensus } from '../lib/activity-cues';
import type { LiveDockDelivery } from '../lib/live-dock-freshness';
import type { LiveStatusEvent } from '../lib/live-status';
import { LiveStatusBar } from './LiveStatusBar';
import { DisclosureChevron } from './DisclosureChevron';
import { AiCapacityStrip } from './AiCapacityStrip';
import type { AiCapacityMeter, UsageAdvisory } from '../lib/ai-usage-view';
import { databaseHealthFacts, type SystemConnectorType, type SystemMonitorCard } from '../lib/systems-monitor';
import type { MachineCapacity, MachineMeter } from '@brunel/shared';
import '../live-dock.css';

export type LiveDockTab = 'live' | 'overview' | 'activity' | 'machines' | 'usage' | 'systems';

export interface LiveDockActivity {
  readonly id: string;
  readonly label: string;
  readonly detail?: string | null;
  readonly at?: string | null;
}

export interface LiveDockMachine {
  readonly id: string;
  readonly name: string;
  readonly status: string;
  readonly detail?: string | null;
  /**
   * The whole capacity picture for this computer when the room has one (#483). The card
   * stays compact and glanceable either way; with this it also carries the small meters,
   * the seat count and whether the machine is being given new work.
   */
  readonly capacity?: MachineCapacity | null;
}

export interface LiveDockProps {
  readonly events: readonly LiveStatusEvent[];
  readonly attentionCount?: number;
  readonly onWorkerSelect?: (workerId: string) => void;
  readonly overview?: ReactElement;
  readonly activity?: readonly LiveDockActivity[];
  readonly machines?: readonly LiveDockMachine[];
  readonly selectedMachineId?: string | null;
  readonly onMachineSelect?: (nodeId: string) => void;
  readonly usageMeters?: readonly AiCapacityMeter[];
  readonly usageAdvisories?: readonly UsageAdvisory[];
  readonly selectedUsageKey?: string | null;
  readonly onUsageSelect?: (key: string) => void;
  readonly systems?: readonly SystemMonitorCard[];
  readonly selectedSystemType?: SystemConnectorType | null;
  readonly onSystemSelect?: (typeKey: SystemConnectorType) => void;
  readonly expanded: boolean;
  readonly onExpandedChange: (expanded: boolean) => void;
  /** #733. Already derived from the Worker cards. The dock does not fetch or tick. */
  readonly activityIndicator?: BrunelActivityIndicator;
  /** #733 slice 2. Existing channel and Cockpit refresh only. The dock does not poll. */
  readonly delivery?: LiveDockDelivery;
  /** #733. Working / Idle / Waiting / Finished recently, already counted. The dock does not poll. */
  readonly census?: LiveDockCensus;
}

const TAB_LABELS: Readonly<Record<LiveDockTab, string>> = {
  live: 'Live',
  overview: 'How it is going',
  activity: 'Activity',
  machines: 'Machines',
  usage: 'AI Usage',
  systems: 'Systems',
};

/** One glance at whether work is happening. The words carry the state; the dot only echoes them. */
function BrunelActivityMark({
  indicator,
  label,
  stale,
}: {
  readonly indicator: BrunelActivityIndicator;
  readonly label: string;
  readonly stale: boolean;
}): ReactElement {
  const dot = stale ? 'stale' : (indicator.kind === 'none' ? 'idle' : indicator.kind);
  const spoken = ['Brunel activity', label, indicator.lastActivityText]
    .filter((part): part is string => part !== null && part !== '')
    .join('. ');
  const kindClass = stale ? 'brunel-activity-stale' : `brunel-activity-${indicator.kind}`;
  return (
    <p className={`brunel-activity ${kindClass}`} role="status" aria-label={spoken}>
      <span className={`activity-beacon activity-beacon-${dot}`} aria-hidden="true" />
      <span className="brunel-activity-name">Brunel activity</span>
      <strong className="brunel-activity-label">{label}</strong>
      {indicator.lastActivityText !== null && <small className="brunel-activity-age">{indicator.lastActivityText}</small>}
    </p>
  );
}

/** Slice 1 indicator, plus the paused line when delivery has stopped. No timer of its own. */
function LiveDockActivityCluster({
  indicator,
  delivery,
  census,
}: {
  readonly indicator?: BrunelActivityIndicator;
  readonly delivery?: LiveDockDelivery;
  readonly census?: LiveDockCensus;
}): ReactElement | null {
  const stale = delivery?.stale === true;
  if (!indicator && !stale && census === undefined) return null;
  const censusLabel = census === undefined ? null : liveDockCensusLabel(census);
  return (
    <div className="live-dock-activity-cluster">
      {indicator && (
        <BrunelActivityMark
          indicator={indicator}
          label={delivery?.stale === true ? delivery.activityLabel : indicator.label}
          stale={stale}
        />
      )}
      {delivery?.stale === true && (
        <p className="live-dock-paused" role="status">
          <span className="live-dock-paused-label">{delivery.pausedText}</span>
          {delivery.lastUpdatedText !== null && <small className="live-dock-paused-age">{delivery.lastUpdatedText}</small>}
        </p>
      )}
      {censusLabel !== null && (
        <p className="live-dock-census" role="status" title="Working, Idle, and Waiting each count one Worker from its activity beacon. Finished recently counts loaded runs reported in the last 60 minutes.">
          {censusLabel}
        </p>
      )}
    </div>
  );
}

function timestamp(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function LiveDock({
  events,
  attentionCount = 0,
  onWorkerSelect,
  overview,
  activity = [],
  machines = [],
  selectedMachineId = null,
  onMachineSelect,
  usageMeters = [],
  usageAdvisories = [],
  selectedUsageKey = null,
  onUsageSelect,
  systems = [],
  selectedSystemType = null,
  onSystemSelect,
  expanded,
  onExpandedChange,
  activityIndicator,
  delivery,
  census,
}: LiveDockProps): ReactElement {
  const [tab, setTab] = useState<LiveDockTab>('live');

  const counts: Readonly<Record<LiveDockTab, number>> = {
    live: events.length,
    overview: 0,
    activity: activity.length,
    machines: machines.length,
    usage: usageMeters.length,
    systems: systems.length,
  };

  const selectTab = (next: LiveDockTab) => {
    setTab(next);
    if (!expanded) onExpandedChange(true);
  };

  return (
    <section className={`live-dock${expanded ? ' live-dock-expanded' : ''}`} aria-label="Brunel live dock">
      <button
        type="button"
        className="live-dock-lip"
        aria-label={expanded ? 'Collapse live dock' : 'Expand live dock'}
        aria-expanded={expanded}
        title={expanded ? 'Collapse live dock' : 'Expand live dock'}
        onClick={() => onExpandedChange(!expanded)}
      >
        <DisclosureChevron direction={expanded ? 'down' : 'up'} />
      </button>

      {!expanded ? (
        <div className={`live-dock-collapsed${activityIndicator || delivery?.stale === true || census !== undefined ? ' has-brunel-activity' : ''}`}>
          <LiveDockActivityCluster indicator={activityIndicator} delivery={delivery} census={census} />
          <LiveStatusBar
            events={events}
            maxVisible={1}
            attentionCount={attentionCount}
            onWorkerSelect={onWorkerSelect}
            compact
            ariaLabel="Brunel live status"
          />
        </div>
      ) : (
        <>
          <header className="live-dock-toolbar">
            <LiveDockActivityCluster indicator={activityIndicator} delivery={delivery} census={census} />
            <div className="live-dock-tabs" role="tablist" aria-label="Live dock sections">
              {(Object.keys(TAB_LABELS) as LiveDockTab[]).map((key) => (
                <button
                  type="button"
                  key={key}
                  role="tab"
                  aria-selected={tab === key}
                  className={tab === key ? 'active' : ''}
                  onClick={() => selectTab(key)}
                >
                  {TAB_LABELS[key]}
                  {counts[key] > 0 && <span className="live-dock-tab-count">{counts[key]}</span>}
                </button>
              ))}
            </div>
          </header>

          <div className="live-dock-panel" role="tabpanel" aria-label={TAB_LABELS[tab]}>
            {tab === 'live' && (
              <LiveStatusBar
                events={events}
                maxVisible={8}
                attentionCount={attentionCount}
                onWorkerSelect={onWorkerSelect}
                ariaLabel="Brunel live status"
              />
            )}

            {tab === 'overview' && overview && (
              <div className="live-dock-overview" aria-label="How it is going snapshot">
                {overview}
              </div>
            )}

            {tab === 'activity' && (
              <div className="live-dock-list">
                {activity.length === 0 && <p className="muted">No recent Worker activity.</p>}
                {activity.map((item) => (
                  <article key={item.id} className="live-dock-row">
                    <span className="stack">
                      <strong>{item.label}</strong>
                      {item.detail && <small className="muted">{item.detail}</small>}
                    </span>
                    {timestamp(item.at) && <time className="muted">{timestamp(item.at)}</time>}
                  </article>
                ))}
              </div>
            )}

            {tab === 'machines' && (
              <MachineGlance machines={machines} selected={selectedMachineId} onSelect={onMachineSelect} />
            )}

            {tab === 'usage' && (
              <div className="live-dock-usage">
                <AiCapacityStrip
                  meters={usageMeters}
                  advisories={usageAdvisories}
                  compact
                  selectedKey={selectedUsageKey}
                  onSelect={onUsageSelect}
                  empty="No AI usage readings yet. Unknown capacity stays Unknown until a provider or runtime reports it."
                />
              </div>
            )}

            {tab === 'systems' && (
              <SystemsGlance systems={systems} selected={selectedSystemType} onSelect={onSystemSelect} />
            )}
          </div>
        </>
      )}
    </section>
  );
}

/**
 * The compact horizontal machine cards in the bottom Machines tab (#483).
 *
 * One card per computer: its name, whether it is in touch, how many of its Worker seats
 * are in use, whether it is being given new work, and a small meter per dimension it can
 * actually measure. A dimension it cannot measure says Unknown and draws no bar at all -
 * an empty meter and an unmeasured one are different things, and only one of them is true.
 *
 * Selecting a card opens that machine's Capacity detail in the existing right drawer.
 * Nothing is decided here and nothing is written here.
 */
export function MachineGlance({
  machines,
  selected = null,
  onSelect,
}: {
  readonly machines: readonly LiveDockMachine[];
  readonly selected?: string | null;
  readonly onSelect?: (nodeId: string) => void;
}): ReactElement {
  if (machines.length === 0) {
    return (
      <div className="live-dock-machine-grid" aria-label="Machines">
        <p className="muted">No computers are reporting yet.</p>
      </div>
    );
  }
  return (
    <div className="live-dock-machine-grid" aria-label="Machines">
      {machines.map((machine) => {
        const capacity = machine.capacity ?? null;
        const on = selected === machine.id;
        return (
          <button
            type="button"
            key={machine.id}
            className={on ? 'live-dock-machine selected' : 'live-dock-machine'}
            data-node={machine.id}
            data-stance={capacity?.stance ?? 'unknown'}
            aria-pressed={on}
            title={capacity?.dispatchLine ?? `Open the capacity detail for ${machine.name}`}
            onClick={() => onSelect?.(machine.id)}
          >
            <span className="row spread">
              <strong>{machine.name}</strong>
              <span className="tag" data-presence={capacity?.presence ?? machine.status}>{machine.status}</span>
            </span>
            {capacity === null ? (
              machine.detail !== null && machine.detail !== undefined
                ? <small className="muted">{machine.detail}</small> : null
            ) : (
              <>
                <span className="row spread live-dock-machine-line">
                  <small className="muted">Workers {capacity.seatsText}</small>
                  <small className={`machine-stance tone-${capacity.stance === 'accepting' ? 'ok' : capacity.stance === 'unknown' ? 'unknown' : 'attention'}`}>
                    Taking work: {capacity.stanceLabel}
                  </small>
                </span>
                <span className="machine-meters">
                  {capacity.meters.filter((meter) => meter.key !== 'seats').map((meter) => (
                    <MachineMeterBar key={meter.key} meter={meter} />
                  ))}
                </span>
                <small className="muted machine-foot">
                  {[
                    capacity.holdBackReason,
                    capacity.versionLine,
                    capacity.recentRuns === null
                      ? null
                      : `${capacity.recentRuns} run${capacity.recentRuns === 1 ? '' : 's'} in the last ${capacity.recentWindowMinutes}m`,
                    capacity.readingAgeLabel === null ? 'no reading yet' : `read ${capacity.readingAgeLabel}`,
                  ].filter((part): part is string => part !== null && part !== undefined).join(' · ')}
                </small>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * One small meter. A reading the machine cannot take draws no bar and says Unknown; a
 * reading it can take draws the bar and, where the owner set a limit on that dimension,
 * marks where the limit sits.
 */
export function MachineMeterBar({ meter }: { readonly meter: MachineMeter }): ReactElement {
  return (
    <span className={`machine-meter tone-${meter.tone}`} data-meter={meter.key} data-known={meter.known ? 'true' : 'false'}
      title={meter.detail ?? `${meter.label} ${meter.text}`}>
      <span className="machine-meter-label">{meter.label}</span>
      <span className="machine-meter-track" role="presentation">
        {meter.known && meter.percent !== null && (
          <span className="machine-meter-fill" style={{ width: `${Math.max(0, Math.min(100, meter.percent))}%` }} />
        )}
        {meter.known && meter.limitPercent !== null && (
          <span className="machine-meter-limit" style={{ left: `${Math.max(0, Math.min(100, meter.limitPercent))}%` }} />
        )}
      </span>
      <span className="machine-meter-value">{meter.text}</span>
    </span>
  );
}

export function SystemsGlance({
  systems,
  selected = null,
  onSelect,
}: {
  readonly systems: readonly SystemMonitorCard[];
  readonly selected?: SystemConnectorType | null;
  readonly onSelect?: (typeKey: SystemConnectorType) => void;
}): ReactElement {
  return (
    <div className="live-dock-systems" aria-label="System health glance">
      {systems.map((system) => (
        <div
          key={system.typeKey}
          className={selected === system.typeKey ? 'live-dock-system selected' : 'live-dock-system'}
          data-system={system.typeKey}
        >
          <button
            type="button"
            className="live-dock-system-open"
            aria-pressed={selected === system.typeKey}
            onClick={() => onSelect?.(system.typeKey)}
          >
            <span className="row spread">
              <strong>{system.label}</strong>
              <span className="system-status" data-status={system.status.toLowerCase()}>{system.status}</span>
            </span>
            <small>{system.summary}</small>
            <span className="muted">Source: {system.source}</span>
          </button>
          {system.typeKey === 'supabase_project' && (
            <dl className="live-dock-system-facts">
              {databaseHealthFacts(system).map((fact) => (
                <div key={fact.id}>
                  <dt>{fact.label}</dt>
                  <dd>
                    {fact.value}
                    {fact.detail ? <small>{fact.detail}</small> : null}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      ))}
    </div>
  );
}
